import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { auth } from './firebase';
import { Box, Typography, TextField, Button, CircularProgress } from '@mui/material';

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as any)?.from?.pathname ?? '/';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    setError('');
    setLoading(true);
    try {
      await signInWithEmailAndPassword(auth, email, password);
      navigate(from, { replace: true });
    } catch (err: any) {
      setError('Invalid email or password.');
      setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSubmit();
  };

  return (
    <Box sx={{
      minHeight: '100vh',
      backgroundColor: '#0d1b2a',
      fontFamily: '"IBM Plex Sans", "Helvetica Neue", sans-serif',
      backgroundImage: `
        linear-gradient(rgba(240,165,0,0.03) 1px, transparent 1px),
        linear-gradient(90deg, rgba(240,165,0,0.03) 1px, transparent 1px)
      `,
      backgroundSize: '48px 48px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@300;400;500;600&family=IBM+Plex+Mono:wght@400;500&display=swap');`}</style>

      <Box sx={{
        width: '100%',
        maxWidth: '420px',
        mx: 3,
      }}>

        {/* Logo */}
        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 6 }}>
          <Box
            component="img"
            src="/logo.png"
            alt="Bahn Stuttgart Logo"
            sx={{ height: 48, opacity: 0.9 }}
          />
        </Box>

        {/* Card */}
        <Box sx={{
          backgroundColor: 'rgba(255,255,255,0.03)',
          border: '1px solid rgba(255,255,255,0.08)',
          borderRadius: '8px',
          padding: '40px',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0, left: 0, right: 0,
            height: '2px',
            background: 'linear-gradient(90deg, #f0a500, transparent)',
          }
        }}>

          {/* Header */}
          <Typography sx={{
            fontFamily: '"IBM Plex Mono"',
            fontSize: '11px',
            letterSpacing: '0.2em',
            color: '#f0a500',
            textTransform: 'uppercase',
            mb: 1,
          }}>
            Stuttgart S-Bahn
          </Typography>
          <Typography sx={{
            fontFamily: '"IBM Plex Sans"',
            fontSize: '22px',
            fontWeight: 300,
            color: '#ffffff',
            mb: 1,
          }}>
            Anmelden
          </Typography>
          <Typography sx={{
            fontFamily: '"IBM Plex Sans"',
            fontSize: '13px',
            fontWeight: 300,
            color: 'rgba(255,255,255,0.35)',
            mb: 4,
          }}>
            Sign in to access the dashboard
          </Typography>

          {/* Fields */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              fullWidth
              type="email"
              placeholder="Email address"
              value={email}
              onChange={e => setEmail(e.target.value)}
              onKeyDown={handleKeyDown}
              autoComplete="email"
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  fontFamily: '"IBM Plex Sans"',
                  fontSize: '14px',
                  color: '#ffffff',
                  backgroundColor: 'rgba(255,255,255,0.04)',
                  borderRadius: '4px',
                  '& fieldset': { borderColor: 'rgba(255,255,255,0.12)' },
                  '&:hover fieldset': { borderColor: 'rgba(240,165,0,0.4)' },
                  '&.Mui-focused fieldset': { borderColor: '#f0a500', borderWidth: '1px' },
                },
                '& input::placeholder': { color: 'rgba(255,255,255,0.25)', opacity: 1 },
                '& input:-webkit-autofill': {
                  WebkitBoxShadow: '0 0 0 100px #0d1b2a inset',
                  WebkitTextFillColor: '#ffffff',
                },
              }}
            />
            <TextField
              fullWidth
              type="password"
              placeholder="Password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              onKeyDown={handleKeyDown}
              autoComplete="current-password"
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  fontFamily: '"IBM Plex Sans"',
                  fontSize: '14px',
                  color: '#ffffff',
                  backgroundColor: 'rgba(255,255,255,0.04)',
                  borderRadius: '4px',
                  '& fieldset': { borderColor: 'rgba(255,255,255,0.12)' },
                  '&:hover fieldset': { borderColor: 'rgba(240,165,0,0.4)' },
                  '&.Mui-focused fieldset': { borderColor: '#f0a500', borderWidth: '1px' },
                },
                '& input::placeholder': { color: 'rgba(255,255,255,0.25)', opacity: 1 },
                '& input:-webkit-autofill': {
                  WebkitBoxShadow: '0 0 0 100px #0d1b2a inset',
                  WebkitTextFillColor: '#ffffff',
                },
              }}
            />
          </Box>

          {/* Error */}
          {error && (
            <Typography sx={{
              fontFamily: '"IBM Plex Sans"',
              fontSize: '13px',
              color: '#ff6b6b',
              mt: 2,
              mb: 0,
            }}>
              {error}
            </Typography>
          )}

          {/* Submit */}
          <Button
            fullWidth
            onClick={handleSubmit}
            disabled={loading || !email || !password}
            sx={{
              mt: 3,
              py: 1.5,
              backgroundColor: '#f0a500',
              color: '#0d1b2a',
              fontFamily: '"IBM Plex Sans"',
              fontWeight: 600,
              fontSize: '14px',
              letterSpacing: '0.05em',
              textTransform: 'none',
              borderRadius: '4px',
              '&:hover': { backgroundColor: '#d4920a' },
              '&:disabled': { backgroundColor: 'rgba(240,165,0,0.2)', color: 'rgba(255,255,255,0.2)' },
            }}
          >
            {loading ? <CircularProgress size={20} sx={{ color: '#0d1b2a' }} /> : 'Sign in'}
          </Button>
        </Box>

        {/* Footer */}
        <Typography sx={{
          fontFamily: '"IBM Plex Mono"',
          fontSize: '11px',
          color: 'rgba(255,255,255,0.15)',
          textAlign: 'center',
          mt: 4,
          letterSpacing: '0.1em',
        }}>
          STUTTGART S-BAHN · OCCUPANCY INTELLIGENCE
        </Typography>
      </Box>
    </Box>
  );
};

export default LoginPage;